import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PencilSimple, Receipt, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { COMPANY_BILLING_MODEL_LABEL } from "@/lib/status-configs";
import { expiryLabel, expiryState, formatLocalYmd, todayYmd } from "@/lib/company-expiry";
import type { Company } from "@/types";
import { BillingTermsDialog, type BillingTermsPatch } from "./BillingTermsDialog";

function Term({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-foreground">{children}</dd>
    </div>
  );
}

function Expiry({ ymd }: { ymd: string | null }) {
  if (!ymd) return <span className="text-muted-foreground">Bez isteka</span>;
  const today = todayYmd();
  const state = expiryState(ymd, today);
  return (
    <span>
      {formatLocalYmd(ymd)}
      {state !== "ok" && (
        <span className={cn("ml-1.5 text-xs font-semibold", state === "expired" ? "text-rose-600" : "text-amber-600")}>
          {expiryLabel(ymd, today)}
        </span>
      )}
    </span>
  );
}

export function BillingTermsSection({ company }: { company: Company }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const save = useMutation({
    mutationFn: async (patch: BillingTermsPatch) => {
      const res = await fetch(`/api/companies/${company.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      await throwIfNotOk(res, "Greška pri spremanju uslova naplate");
      return res.json();
    },
    onSuccess: () => {
      toast.success("Uslovi naplate spremljeni");
      queryClient.invalidateQueries({ queryKey: ["admin", "company", company.id] });
      queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
      setEditing(false);
    },
  });

  const missingLegal = !company.legalName || !company.address || !company.city || !company.taxId;

  return (
    <div className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Receipt className="size-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">Uslovi naplate</h2>
        </div>
        <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
          <PencilSimple className="size-4" /> Uredi
        </Button>
      </div>

      <dl className="mt-4 grid gap-4 sm:grid-cols-3">
        <Term label="Model naplate">{COMPANY_BILLING_MODEL_LABEL[company.billingModel]}</Term>
        <Term label="Firma plaća">
          {company.cofundingPercent >= 100 ? "Cijelu seansu" : `${company.cofundingPercent} %, zaposleni ostatak`}
        </Term>
        <Term label="Rabat">{company.discountPercent > 0 ? `${company.discountPercent} % (iz provizije)` : "Bez rabata"}</Term>
        <Term label="Ugovor ističe"><Expiry ymd={company.contractExpiresAt} /></Term>
        <Term label="Kredit ističe"><Expiry ymd={company.creditExpiresAt} /></Term>
        <Term label="Rok plaćanja">{company.paymentTermDays} dana</Term>
      </dl>

      {missingLegal && (
        <p className="mt-4 flex items-center gap-1.5 text-xs text-amber-700">
          <Warning className="size-3.5" />
          Nedostaju pravni podaci firme; bez njih se ne može izdati faktura ni predračun.
        </p>
      )}

      {editing && (
        <BillingTermsDialog
          company={company}
          isPending={save.isPending}
          onSubmit={(p) => save.mutate(p)}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}
